import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { customPushService } from '../../services/customPushService';
import { ICustomPushNotificationType } from '../../types/customPush';
import NotificationTypeDialog from './NotificationTypeDialog';

// Mirrors customPushReservedNotificationTypes in the backend
// (service/custom_push_notification_type.go) - these can't be created,
// edited, or deactivated through the custom type CRUD, so their row hides
// those actions instead of letting an admin hit a 400 from the API.
// echo_pay is pre-seeded (migration 000014) specifically so it's always
// visible here and selectable when assigning an API key.
const RESERVED_NOTIFICATION_TYPES = new Set(['coupon', 'notification', 'notification_deleted', 'echo_pay']);

const isReserved = (notificationType: string) => RESERVED_NOTIFICATION_TYPES.has(notificationType.toLowerCase());

interface NotificationTypesTabProps {
  canManage: boolean;
}

const NotificationTypesTab: React.FC<NotificationTypesTabProps> = ({ canManage }) => {
  const [notificationTypes, setNotificationTypes] = useState<ICustomPushNotificationType[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selected, setSelected] = useState<ICustomPushNotificationType | null>(null);

  const loadNotificationTypes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await customPushService.getNotificationTypes();
      setNotificationTypes(data);
    } catch (error) {
      console.error('Error loading notification types:', error);
      setNotificationTypes([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotificationTypes();
  }, [loadNotificationTypes]);

  const handleNew = () => {
    setSelected(null);
    setDialogOpen(true);
  };

  const handleEdit = (notificationType: ICustomPushNotificationType) => {
    setSelected(notificationType);
    setDialogOpen(true);
  };

  const handleDeactivate = async (notificationType: ICustomPushNotificationType) => {
    if (!window.confirm(`Deactivate notification type "${notificationType.notificationType}"?`)) return;
    try {
      await customPushService.deactivateNotificationType(notificationType.notificationType);
      loadNotificationTypes();
    } catch (error) {
      console.error('Error deactivating notification type:', error);
      alert('Network error. Try again later');
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h6" sx={{ fontWeight: 500 }}>
          Notification Types
        </Typography>
        {canManage && (
          <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={handleNew}>
            New Type
          </Button>
        )}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 500 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Notification Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  {canManage && <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {notificationTypes.map((type) => (
                  <TableRow key={type.notificationType} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{type.notificationType}</TableCell>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{type.description}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        <Chip
                          label={type.isActive ? 'Active' : 'Inactive'}
                          color={type.isActive ? 'success' : 'default'}
                          size="small"
                        />
                        {isReserved(type.notificationType) && (
                          <Tooltip title="Used internally - not editable or removable here">
                            <Chip label="Reserved" size="small" variant="outlined" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        {isReserved(type.notificationType) ? (
                          <Typography variant="body2" color="text.secondary">
                            System-managed
                          </Typography>
                        ) : (
                          <Box display="flex" gap={1}>
                            <Tooltip title="Edit">
                              <IconButton size="small" color="primary" onClick={() => handleEdit(type)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {type.isActive && (
                              <Tooltip title="Deactivate">
                                <IconButton size="small" color="error" onClick={() => handleDeactivate(type)}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {notificationTypes.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={canManage ? 4 : 3}>
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                        No notification types configured yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {canManage && (
        <NotificationTypeDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          notificationType={selected}
          onSaved={loadNotificationTypes}
        />
      )}
    </Box>
  );
};

export default NotificationTypesTab;
